import { Alert, Box, Card, Slider, Stack, Typography } from '@mui/material';
import { listings } from '@prisma/client';
import { formatCurrency } from '../../utils/formatCurrency';
import { useEffect, useRef, useState } from 'react';
import { getEnv } from '../../utils/env';

interface Props {
  listing: listings;
  offerPrice: number;
  handlePriceChange: (price: number) => void;
}

const BuyingPower: React.FC<Props> = ({
  listing,
  offerPrice,
  handlePriceChange,
}) => {
  const homepointFee = Number(getEnv('HOMEPOINT_FEE'));
  const buyingPower =
    listing.price -
    listing.price * ((100 - listing.commission) / 100) -
    homepointFee;
  // Start the slider where the offer price already sits, so a price carried in
  // from an earlier step (e.g. a chosen offer scenario) is reflected instead of
  // being overwritten.
  const startingMorePower = Number.isFinite(offerPrice)
    ? Math.min(
        Math.max(buyingPower - (listing.price - offerPrice), 0),
        buyingPower
      )
    : buyingPower;
  const [morePower, setMorePower] = useState(startingMorePower);
  const diff = buyingPower - morePower;
  const isFirstRun = useRef(true);

  useEffect(() => {
    // Only a slider move should rewrite the offer price. The mount run used to
    // push list price into the form, which overwrote any price the caller had
    // seeded (the market analysis "Draft an offer" CTA opens the wizard at its
    // suggested number) and wrote NaN whenever HOMEPOINT_FEE was unset.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (!Number.isFinite(diff)) return;
    handlePriceChange(listing.price - diff);
  }, [morePower]);

  return (
    <Card elevation={2} sx={{ marginTop: 1 }}>
      <Alert severity="info">
        Most home prices have typical commissions built into the price.
      </Alert>
      <Box p={2}>
        <Typography variant="body2">
          Homepoint will verify if the seller is offering a buyer&apos;s agent
          commission. Since you are using Homepoint, our flat fee is typically
          much lower than the offered commission. You can use this to your
          advantage as your <b>buying power</b>.
        </Typography>
        <Typography variant="body1" textAlign={'center'} fontWeight={600}>
          Buying Power: {formatCurrency(buyingPower)}
        </Typography>
        <Stack
          spacing={2}
          direction="row"
          sx={{ alignItems: 'center', mb: 1, p: 2 }}
        >
          <Typography variant="body2">More Savings</Typography>
          <Slider
            min={0}
            max={buyingPower}
            value={morePower}
            step={1000}
            color="success"
            onChange={(event: any) =>
              setMorePower(
                event.target?.value === '' ? 0 : Number(event.target?.value)
              )
            }
            sx={{ width: '100%', maxWidth: 500 }}
          />
          <Typography variant="body2">More Competitive</Typography>
        </Stack>
        <Typography variant="body2" textAlign={'center'}>
          Your offer price of {formatCurrency(offerPrice)} is{' '}
          {formatCurrency(morePower)} better than offers with{' '}
          {listing.commission}% commissions.
        </Typography>
      </Box>
    </Card>
  );
};

export default BuyingPower;
